import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  color: #FECE1B;
  margin-bottom: 2rem;
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    color: #1a1a1a;
    margin-bottom: 1rem;
    font-size: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h3 {
    color: #333;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
    font-size: 1.25rem;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }

  p {
    margin-bottom: 1rem;
    color: #555;
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      color: #555;
    }
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-top: 0;
    color: #FECE1B;
  }

  p {
    margin-bottom: 0.5rem;
  }
`;

const CookiePolicyPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Cookie Policy | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Cookie policy for ZAN RESIN SURFACES LTD - Information about how we use cookies on our website." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/cookies/" />
      </Helmet>

      <PageContainer>
        <Title>Cookie Policy</Title>

        <Section>
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your computer or mobile device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
          </p>
          <p>
            <strong>Company Details:</strong><br />
            ZAN RESIN SURFACES LTD<br />
            Company Number: 16698534<br />
            Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN<br />
            Email: info@resinsurfaceslondon.co.uk
          </p>
        </Section>

        <Section>
          <h2>2. How We Use Cookies</h2>
          <p>
            We use cookies for several purposes to improve your experience on our website:
          </p>
          <ul>
            <li>To remember your preferences and settings</li>
            <li>To analyze website traffic and user behavior</li>
            <li>To provide personalized content and advertisements</li>
            <li>To ensure website security and functionality</li>
          </ul>
        </Section>

        <Section>
          <h2>3. Types of Cookies We Use</h2>
          
          <h3>3.1 Essential Cookies</h3>
          <p>
            These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas, and form submissions. The website cannot function properly without these cookies.
          </p>
          <ul>
            <li>Session management cookies</li>
            <li>Security cookies</li>
            <li>Load balancing cookies</li>
          </ul>

          <h3>3.2 Analytics Cookies</h3>
          <p>
            We use Google Analytics to understand how visitors interact with our website. These cookies collect information about how you use our site, such as which pages you visit and how long you spend on them.
          </p>
          <ul>
            <li>Google Analytics cookies</li>
            <li>Page view tracking</li>
            <li>User behavior analysis</li>
          </ul>

          <h3>3.3 Marketing Cookies</h3>
          <p>
            These cookies are used to track visitors across websites to display relevant and engaging advertisements. They help us measure the effectiveness of our advertising campaigns.
          </p>
          <ul>
            <li>Social media tracking pixels</li>
            <li>Advertising platform cookies</li>
            <li>Remarketing cookies</li>
          </ul>
        </Section>

        <Section>
          <h2>4. Third-Party Cookies</h2>
          <p>
            Some cookies on our website are set by third-party services that appear on our pages. These include:
          </p>
          <ul>
            <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
            <li><strong>Social Media Platforms:</strong> Facebook, Instagram, LinkedIn for social sharing</li>
            <li><strong>Payment Processors:</strong> For secure payment processing</li>
            <li><strong>Marketing Platforms:</strong> For advertising and remarketing</li>
          </ul>
        </Section>

        <Section>
          <h2>5. Managing Your Cookie Preferences</h2>
          
          <h3>5.1 Browser Settings</h3>
          <p>
            You can control and delete cookies through your browser settings. Most browsers allow you to:
          </p>
          <ul>
            <li>View what cookies are stored on your device</li>
            <li>Delete individual or all cookies</li>
            <li>Block cookies from specific websites</li>
            <li>Block third-party cookies</li>
            <li>Clear all cookies when you close your browser</li>
          </ul>

          <h3>5.2 Cookie Consent Banner</h3>
          <p>
            When you first visit our website, you'll see a cookie consent banner. You can choose to accept all cookies, reject non-essential cookies, or customize your preferences.
          </p>
        </Section>

        <Section>
          <h2>6. Impact of Disabling Cookies</h2>
          <p>
            If you choose to disable cookies, some features of our website may not work properly:
          </p>
          <ul>
            <li>You may need to re-enter information repeatedly</li>
            <li>Some personalized features may not be available</li>
            <li>We won't be able to remember your preferences</li>
            <li>Some forms and interactive elements may not function correctly</li>
          </ul>
        </Section>

        <Section>
          <h2>7. Cookie Retention</h2>
          <p>
            Different cookies have different lifespans:
          </p>
          <ul>
            <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
            <li><strong>Persistent Cookies:</strong> Remain until they expire or you delete them</li>
            <li><strong>Analytics Cookies:</strong> Typically retained for 26 months</li>
            <li><strong>Marketing Cookies:</strong> Vary by provider, usually 30-90 days</li>
          </ul>
        </Section>

        <Section>
          <h2>8. Updates to This Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices or for legal reasons. We will notify you of any significant changes by posting the updated policy on our website.
          </p>
        </Section>

        <ContactInfo>
          <h3>Contact Us</h3>
          <p>If you have any questions about our use of cookies, please contact us:</p>
          <p><strong>ZAN RESIN SURFACES LTD</strong></p>
          <p>Company Number: 16698534</p>
          <p>Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN</p>
          <p>Email: <a href="mailto:info@resinsurfaceslondon.co.uk">info@resinsurfaceslondon.co.uk</a></p>
          <p>Phone: <a href="tel:+447535935779">07535 935 779</a></p>
          <p><em>Last updated: January 2025</em></p>
        </ContactInfo>
      </PageContainer>
    </>
  );
};

export default CookiePolicyPage;
